import { useShopI18n } from '../i18n/ShopI18n'
import { FormEvent, useState } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { authService } from '../services/auth'
import { logger } from '../utils/logger'
import { shopArtwork } from './shopArtwork'
import ShopBrand from './ShopBrand'
import '../shop.css'
import './entry-ui.css'

interface LoginFormProps {
    onSuccess: () => void
    notice?: string | null
    /** UI preview can supply a local handler; live login always uses authService. */
    authenticate?: (email: string, password: string) => Promise<unknown>
    socialProviders?: { name: string; icon?: string; onLogin: () => void }[]
    onForgotPassword?: () => void
    onRegister?: () => void
}

export default function LoginForm({ onSuccess, notice, authenticate = authService.loginWithCredentials.bind(authService), socialProviders, onForgotPassword, onRegister }: LoginFormProps) {
    const { t } = useShopI18n()
    const { themeName } = useTheme()
    const [error, setError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (isSubmitting) return
        // Read the actual inputs: password managers can fill them without change events.
        const form = event.currentTarget
        const fields = new FormData(form)
        const email = String(fields.get('email') ?? '').trim()
        const password = String(fields.get('password') ?? '')
        setError(null)
        if (!email.trim() || !password) {
            setError(t('Bitte gib deine E-Mail-Adresse und dein Passwort ein.'))
            return
        }
        setIsSubmitting(true)
        try {
            await authenticate(email.trim(), password)
            form.reset()
            onSuccess()
        } catch (err) {
            logger.warn('AUTH', 'Login form submission failed')
            setError(err instanceof Error ? t(err.message) : t("Anmeldung fehlgeschlagen. Bitte versuche es erneut."))
        } finally {
            setIsSubmitting(false)
        }
    }
    return <div className="web-shop shop-login-page" data-theme={themeName}>
        <form className="shop-login" onSubmit={handleSubmit} noValidate aria-labelledby="login-heading">
            <div className="shop-login-logo-slot"><ShopBrand className="shop-login-logo" /></div>
            <h1 id="login-heading">{t("Anmelden und einkaufen")}</h1>
            <p className="shop-login-intro">{t("Melde dich mit deinem Spielkonto an. Alles, was du hier kaufst, liegt sofort im Spiel bereit.")}</p>
            {notice && <p className="shop-login-notice" data-testid="login-notice">{t(notice)}</p>}
            {!!socialProviders?.length && <><div className="shop-social">{socialProviders.map(provider => <button type="button" key={provider.name} onClick={provider.onLogin}>{(provider.icon || /^(facebook|apple|google)$/i.test(provider.name)) && <span className="shop-social-icon" data-provider={provider.name.toLowerCase()}><img src={provider.icon || `/assets/webshop/social-${provider.name.toLowerCase()}.png`} alt="" /></span>}{t('Weiter mit {{provider}}', { provider: provider.name })}</button>)}</div><div className="shop-login-divider">{t("oder mit E-Mail")}</div></>}
            <div className="shop-login-fields">
                <label htmlFor="login-email">{t("E-Mail-Adresse")}</label>
                <input id="login-email" name="email" type="email" autoComplete="username" inputMode="email" placeholder={t("name@beispiel.de")} disabled={isSubmitting} aria-describedby={error ? 'login-error' : undefined} />
                <label htmlFor="login-password">{t("Passwort")}</label>
                <input id="login-password" name="password" type="password" autoComplete="current-password" placeholder={t("Passwort")} disabled={isSubmitting} aria-describedby={error ? 'login-error' : undefined} />
            </div>
            {error && <p className="shop-login-error" role="alert" id="login-error" data-testid="login-error">{error}</p>}
            <button type="submit" className="shop-price" disabled={isSubmitting}>{isSubmitting ? t("Anmeldung läuft…") : t("Anmelden")}</button>
            <div className="shop-login-links">{onForgotPassword && <button type="button" onClick={onForgotPassword}>{t("Passwort vergessen")}</button>}{onRegister && <button type="button" onClick={onRegister}>{t("Noch kein Konto?")}</button>}</div>
            <div className="shop-login-info"><img src={shopArtwork.coin} alt="" /><p>{t("Gekaufte Chips und Joker werden dem verbundenen Spielkonto gutgeschrieben. Sie sind virtuelle Spielwährung und lassen sich nicht auszahlen.")}</p></div>
        </form>
    </div>
}
